import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { authAPI, userRolesAPI, User } from "../services/api";
import {
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  setLocalRefreshToken,
  clearLocalStorage,
  getUserInfo,
  setLocalUserInfo,
} from "@/lib/storeManager";

interface AuthContextType {
  user: User | null;
  session: { access_token: string; refresh_token: string } | null;
  userRole: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
  updateAuth: (
    userData: User,
    accessToken: string,
    refreshToken: string
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<{
    access_token: string;
    refresh_token: string;
  } | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken && refreshToken) {
        // Get user info from stored data (set during login/signup)
        const storedUser = getUserInfo();
        if (storedUser) {
          setUser(storedUser);
          setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          // Fetch user role
          if (storedUser.id) {
            await fetchUserRole(storedUser.id);
          } else {
            setLoading(false);
          }
        } else {
          // No stored user info, but token exists - might be from page refresh
          // You could decode JWT here to get user ID, or require re-login
          setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      // If there's an error, try to refresh token if refresh token exists
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await authAPI.refreshToken(refreshToken);
          setLocalAccessToken(response.access);

          // Try to get user info from stored user
          const storedUser = getUserInfo();
          if (storedUser) {
            setUser(storedUser);
            if (storedUser.id) {
              await fetchUserRole(storedUser.id);
            } else {
              setLoading(false);
            }
          } else {
            setLoading(false);
          }

          setSession({
            access_token: response.access,
            refresh_token: refreshToken,
          });
        } catch (_refreshError) {
          // Refresh failed, clear session
          clearLocalStorage();
          setUser(null);
          setSession(null);
          setUserRole(null);
          setLoading(false);
        }
      } else {
        if (import.meta.env.DEV) {
          console.error("Error checking session:", error);
        }
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Check for existing session on mount
    checkSession();
  }, [checkSession]);

  const fetchUserRole = async (userId: string) => {
    try {
      const roles = await userRolesAPI.getByUserId(userId);
      if (roles && roles.length > 0) {
        setUserRole(roles[0].role);
      } else {
        setUserRole(null);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching user role:", error);
      }
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error signing out:", error);
      }
    } finally {
      clearLocalStorage();
      setUser(null);
      setSession(null);
      setUserRole(null);
    }
  };

  // Expose a method to update user and session (for login/signup)
  const updateAuth = async (
    userData: User,
    accessToken: string,
    refreshToken: string
  ) => {
    setLocalAccessToken(accessToken);
    setLocalRefreshToken(refreshToken);
    setLocalUserInfo(userData); // Store user info for later retrieval
    setUser(userData);
    setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (userData?.id) {
      await fetchUserRole(userData.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, session, userRole, loading, signOut, updateAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error(
      "useAuth must be used within an AuthProvider. " +
        "Make sure your component is wrapped with <AuthProvider>."
    );
  }

  return context;
};
